import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardCheck, ArrowRight, ArrowLeft, CheckCircle, Clock, Brain, 
  TrendingUp, FileText, Users, Target, Megaphone, Calendar, Zap,
  Mail, Phone, MessageSquare, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";
import { API_URL } from '../config/api';
const OG_IMAGE = "https://customer-assets.emergentagent.com/job_4e491698-f834-49a8-a1e5-8fd3efa88feb/artifacts/7ipfzetv_FB_IMG_1772974876810.jpg";

// SEO Meta Tags Component
function DiagnosticSEO() {
  return (
    <Helmet>
      <title>Diagnostic Entrepreneur Gratuit | BatiAssist</title>
      <meta name="description" content="Faites le diagnostic de ton entreprise en 3 minutes. Découvre ton score d'efficacité, le temps que tu perds et comment transformer ton organisation. Outil gratuit." />
      <meta name="keywords" content="diagnostic entrepreneur, test organisation entreprise, score efficacité, temps perdu entrepreneur, charge mentale, automatisation entreprise" />
      <link rel="canonical" href="https://recrutermalin.com/diagnostic-entrepreneur" />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://recrutermalin.com/diagnostic-entrepreneur" />
      <meta property="og:title" content="Diagnostic Entrepreneur Gratuit - Analysez ton organisation en 3 min" />
      <meta property="og:description" content="Combien de temps perds-tu chaque semaine ? Découvre ton score d'efficacité entrepreneuriale et comment transformer ton entreprise. Test gratuit." />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Diagnostic Entrepreneur - BatiAssist" />
      <meta property="og:site_name" content="BatiAssist" />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content="https://recrutermalin.com/diagnostic-entrepreneur" />
      <meta name="twitter:title" content="Diagnostic Entrepreneur Gratuit - Testez ton efficacité" />
      <meta name="twitter:description" content="En 3 minutes, découvre ton score d'efficacité et de liberté entrepreneuriale. Combien de temps perds-tu vraiment ?" />
      <meta name="twitter:image" content={OG_IMAGE} />
      <meta name="twitter:image:alt" content="Diagnostic Entrepreneur - BatiAssist" />
    </Helmet>
  );
}

// Diagnostic steps configuration
const diagnosticSteps = [
  {
    id: 1,
    title: "Gestion administrative de ton entreprise",
    icon: FileText,
    tasks: [
      { id: 'devis', label: 'Préparer et envoyer tes devis' },
      { id: 'factures', label: 'Créer et envoyer tes factures' },
      { id: 'comptes', label: 'Vérifier tes comptes bancaires' },
      { id: 'relances', label: 'Relancer les factures impayées' },
      { id: 'classement', label: 'Classer tes documents administratifs' },
      { id: 'emails', label: 'Répondre aux emails' },
      { id: 'tri_mail', label: 'Trier ta boîte mail' },
      { id: 'agenda', label: 'Organiser ton agenda' },
      { id: 'dossiers_clients', label: 'Gérer les dossiers clients' },
    ]
  },
  {
    id: 2,
    title: "Gestion des clients",
    icon: Users,
    tasks: [
      { id: 'appels', label: 'Répondre aux appels entrants' },
      { id: 'demandes_devis', label: 'Répondre aux demandes de devis' },
      { id: 'sav', label: 'Gérer le service après-vente' },
      { id: 'rdv_clients', label: 'Organiser les rendez-vous clients' },
      { id: 'whatsapp', label: 'Répondre aux messages WhatsApp' },
      { id: 'messages_rs', label: 'Répondre aux messages sur les réseaux sociaux' },
    ]
  },
  {
    id: 3,
    title: "Trouver de nouveaux clients",
    icon: Target,
    tasks: [
      { id: 'chercher_prospects', label: 'Chercher de nouveaux prospects' },
      { id: 'contacter_prospects', label: 'Contacter des prospects' },
      { id: 'messages_prospection', label: 'Envoyer des messages de prospection' },
      { id: 'relancer_prospects', label: 'Relancer des prospects' },
      { id: 'reseau_pro', label: 'Développer ton réseau professionnel' },
      { id: 'suivi_commercial', label: 'Organiser ton suivi commercial' },
    ]
  },
  {
    id: 4,
    title: "Visibilité et marketing",
    icon: Megaphone,
    tasks: [
      { id: 'creer_posts', label: 'Créer des publications sur les réseaux sociaux' },
      { id: 'publier_rs', label: 'Publier sur Facebook / Instagram / LinkedIn' },
      { id: 'commentaires', label: 'Répondre aux commentaires' },
      { id: 'mp', label: 'Répondre aux messages privés' },
      { id: 'visuels', label: 'Créer des visuels ou graphismes' },
      { id: 'site_web', label: 'Mettre à jour ton site internet' },
      { id: 'contenu', label: 'Rédiger des articles ou du contenu' },
      { id: 'analyser_visibilite', label: 'Analyser ta visibilité en ligne' },
    ]
  },
  {
    id: 5,
    title: "Organisation de ton entreprise",
    icon: Calendar,
    tasks: [
      { id: 'planifier_semaine', label: 'Planifier ta semaine' },
      { id: 'urgences', label: 'Gérer les urgences' },
      { id: 'coordonner_equipes', label: 'Coordonner les équipes' },
      { id: 'suivi_projets', label: 'Suivre les chantiers ou projets' },
      { id: 'chercher_infos', label: 'Chercher des informations' },
      { id: 'taches_quotidiennes', label: 'Organiser tes tâches quotidiennes' },
    ]
  },
  {
    id: 6,
    title: "Ta charge mentale d'entrepreneur",
    icon: Brain,
    tasks: [
      { id: 'interruptions', label: 'Combien d\'interruptions subis-tu par jour ?', type: 'number', unit: 'fois/jour' },
      { id: 'notifications', label: 'Reçois-tu beaucoup de notifications liées à ton entreprise ?', type: 'scale' },
      { id: 'stress', label: 'Ton niveau de stress lié à ton entreprise', type: 'scale' },
      { id: 'surcharge', label: 'Ton niveau de surcharge mentale', type: 'scale' },
      { id: 'deconnexion', label: 'Ta difficulté à déconnecter du travail', type: 'scale' },
    ]
  }
];

const whoOptions = [
  { value: 'moi', label: 'Je le fais moi-même' },
  { value: 'salarie', label: 'Un salarié le fait' },
  { value: 'prestataire', label: 'Une agence / prestataire' },
  { value: 'personne', label: 'Personne ne s\'en occupe' },
];

// Task Input Component
function TaskInput({ task, value, onChange, stepId }) {
  const isScaleType = task.type === 'scale';
  const isNumberType = task.type === 'number';

  if (isScaleType) {
    return (
      <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-rm-primary/30 transition-colors">
        <p className="text-sm font-medium text-rm-text mb-3">{task.label}</p>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((num) => (
            <button
              key={num}
              onClick={() => onChange(task.id, 'scale', num)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                value?.scale === num 
                  ? 'bg-rm-primary text-white' 
                  : 'bg-slate-100 text-rm-text-secondary hover:bg-slate-200'
              }`}
            >
              {num}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-rm-text-secondary mt-1">
          <span>Très faible</span>
          <span>Très élevé</span>
        </div>
      </div>
    );
  }

  if (isNumberType) {
    return (
      <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-rm-primary/30 transition-colors">
        <p className="text-sm font-medium text-rm-text mb-3">{task.label}</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={value?.time || ''}
            onChange={(e) => onChange(task.id, 'time', e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 focus:border-rm-primary"
            placeholder="0"
          />
          <span className="text-sm text-rm-text-secondary">{task.unit}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-rm-primary/30 transition-colors">
      <p className="text-sm font-medium text-rm-text mb-4">{task.label}</p>
      
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Temps passé */}
        <div>
          <label className="block text-xs text-rm-text-secondary mb-1">Temps passé</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              value={value?.time || ''}
              onChange={(e) => onChange(task.id, 'time', e.target.value)}
              className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
              placeholder="0"
            />
            <select
              value={value?.timeUnit || 'min_semaine'}
              onChange={(e) => onChange(task.id, 'timeUnit', e.target.value)}
              className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
            >
              <option value="min_jour">min/jour</option>
              <option value="h_jour">h/jour</option>
              <option value="min_semaine">min/semaine</option>
              <option value="h_semaine">h/semaine</option>
            </select>
          </div>
        </div>

        {/* Qui réalise */}
        <div>
          <label className="block text-xs text-rm-text-secondary mb-1">Qui réalise cette tâche ?</label>
          <select
            value={value?.who || 'moi'}
            onChange={(e) => onChange(task.id, 'who', e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          >
            {whoOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Coût mensuel (si délégué) */}
        {(value?.who === 'salarie' || value?.who === 'prestataire') && (
          <div>
            <label className="block text-xs text-rm-text-secondary mb-1">Coût mensuel (€)</label>
            <input
              type="number"
              min="0"
              value={value?.cost || ''}
              onChange={(e) => onChange(task.id, 'cost', e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
              placeholder="0"
            />
          </div>
        )}

        {/* Satisfaction */}
        <div>
          <label className="block text-xs text-rm-text-secondary mb-1">Satisfaction (1-5)</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((num) => (
              <button
                key={num}
                onClick={() => onChange(task.id, 'satisfaction', num)}
                className={`flex-1 py-1.5 rounded text-xs font-medium transition-all ${
                  value?.satisfaction === num 
                    ? 'bg-rm-primary text-white' 
                    : 'bg-slate-100 text-rm-text-secondary hover:bg-slate-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Score Gauge Component
function ScoreGauge({ score, label, color, interpretation }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-rm-text">{label}</h3>
        <span className={`text-3xl font-bold ${color}`}>{score}/100</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-3">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 1, delay: 0.3 }}
          className={`h-full rounded-full ${color === 'text-rm-primary' ? 'bg-rm-primary' : 'bg-rm-secondary'}`}
        />
      </div>
      <p className={`text-sm font-medium ${color}`}>{interpretation}</p>
    </div>
  );
}

export default function DiagnosticEntrepreneurPage() {
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-6 = steps, 7 = email form, 8 = results
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [prenom, setPrenom] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const handleAnswerChange = (taskId, field, value) => {
    setAnswers(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value
      }
    }));
  };

  const calculateScores = () => {
    let totalTimeMinutes = 0;
    let totalCost = 0;
    let selfTasksCount = 0;
    let delegatedTasksCount = 0;
    let noOneTasksCount = 0;
    let satisfactionSum = 0;
    let satisfactionCount = 0;
    let mentalLoadScore = 0;

    // Calculate time and costs
    Object.entries(answers).forEach(([taskId, value]) => {
      if (value?.time) {
        let minutes = parseFloat(value.time) || 0;
        const unit = value.timeUnit || 'min_semaine';
        
        // Convert to minutes per week
        if (unit === 'min_jour') minutes = minutes * 5;
        else if (unit === 'h_jour') minutes = minutes * 60 * 5;
        else if (unit === 'h_semaine') minutes = minutes * 60;
        
        totalTimeMinutes += minutes;
      }

      if (value?.cost) {
        totalCost += parseFloat(value.cost) || 0;
      }

      if (value?.who === 'moi') selfTasksCount++;
      else if (value?.who === 'salarie' || value?.who === 'prestataire') delegatedTasksCount++;
      else if (value?.who === 'personne') noOneTasksCount++;

      if (value?.satisfaction) {
        satisfactionSum += value.satisfaction;
        satisfactionCount++;
      }

      if (value?.scale) {
        mentalLoadScore += value.scale;
      }
    });

    const totalTasks = selfTasksCount + delegatedTasksCount + noOneTasksCount;
    const avgSatisfaction = satisfactionCount > 0 ? satisfactionSum / satisfactionCount : 3;

    // Score d'efficacité (0-100)
    // Based on: satisfaction, delegation rate, time management
    const delegationRate = totalTasks > 0 ? (delegatedTasksCount / totalTasks) * 100 : 0;
    const efficiencyScore = Math.min(100, Math.round(
      (avgSatisfaction * 10) + // 10-50 points based on satisfaction
      (delegationRate * 0.3) + // 0-30 points based on delegation
      (Math.max(0, 30 - (totalTimeMinutes / 60))) // 0-30 points (less time = better)
    ));

    // Score de liberté (0-100)
    // Based on: independence from entrepreneur, mental load, automation
    const selfDependencyRate = totalTasks > 0 ? (selfTasksCount / totalTasks) * 100 : 100;
    const mentalLoadAvg = mentalLoadScore / 5; // Average of 5 mental load questions
    const libertyScore = Math.min(100, Math.max(0, Math.round(
      100 - (selfDependencyRate * 0.5) - // Penalty for doing everything yourself
      (mentalLoadAvg * 8) - // Penalty for mental load
      (noOneTasksCount * 3) // Penalty for neglected tasks
    )));

    // Interpretations
    const getEfficiencyInterpretation = (score) => {
      if (score < 30) return "Entreprise sous pression";
      if (score < 60) return "Entreprise fragile";
      if (score < 80) return "Entreprise organisée";
      return "Entreprise structurée";
    };

    const getLibertyInterpretation = (score) => {
      if (score < 30) return "Entreprise dépendante";
      if (score < 60) return "Entreprise fragile";
      if (score < 80) return "Entreprise organisée";
      return "Entreprise libre";
    };

    // Projected scores with BatiAssist
    const projectedEfficiency = Math.min(100, efficiencyScore + 35);
    const projectedLiberty = Math.min(100, libertyScore + 45);

    return {
      totalTimeWeekly: Math.round(totalTimeMinutes / 60 * 10) / 10, // hours
      totalTimeMonthly: Math.round(totalTimeMinutes / 60 * 4 * 10) / 10, // hours
      totalCost,
      efficiencyScore,
      libertyScore,
      efficiencyInterpretation: getEfficiencyInterpretation(efficiencyScore),
      libertyInterpretation: getLibertyInterpretation(libertyScore),
      projectedEfficiency,
      projectedLiberty,
      selfTasksCount,
      delegatedTasksCount,
      noOneTasksCount
    };
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const scores = calculateScores();

    try {
      // Save lead to backend
      await fetch(`${API_URL}/api/diagnostic/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          prenom,
          answers,
          scores
        })
      });
    } catch (error) {
      console.error('Error saving diagnostic:', error);
    }

    setIsSubmitting(false);
    setShowResults(true);
    setCurrentStep(8);
  };

  const scores = calculateScores();

  // Intro screen
  if (currentStep === 0) {
    return (
      <div data-testid="diagnostic-page">
        <DiagnosticSEO />
        {/* Hero */}
        <section className="py-20 md:py-32 gradient-mesh">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-20 h-20 bg-rm-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <ClipboardCheck className="w-10 h-10 text-rm-primary" />
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
                Diagnostic <span className="text-gradient">Entrepreneur</span>
              </h1>
              <p className="text-xl text-rm-text-secondary mb-4">
                Combien de temps perds-tu chaque semaine dans ton entreprise ?
              </p>
              <p className="text-lg text-rm-text-secondary mb-8">
                En quelques minutes, découvre :
              </p>
              <div className="inline-flex flex-col items-start text-left mb-8 space-y-2">
                {[
                  "ton score d'efficacité entrepreneuriale",
                  "ton score de liberté entrepreneuriale", 
                  "les tâches qui te font perdre du temps",
                  "l'organisation qui pourrait transformer ton entreprise"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-rm-text-secondary">{item}</span>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => setCurrentStep(1)}
                className="bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
              >
                Commencer mon diagnostic
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Pour qui */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rm-text text-center mb-8">
              Pour qui est ce diagnostic ?
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {['Les artisans', 'Les entrepreneurs débordés', 'Les dirigeants de petites entreprises', 'Les indépendants qui veulent mieux organiser leur activité'].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-rm-text">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ce que tu vas découvrir */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rm-text text-center mb-8">
              Ce que tu vas découvrir
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: FileText, label: 'Le temps passé sur les tâches administratives' },
                { icon: Users, label: 'La gestion des clients' },
                { icon: Target, label: 'La prospection commerciale' },
                { icon: Megaphone, label: 'La visibilité et le marketing' },
                { icon: Brain, label: 'La charge mentale liée à l\'entreprise' },
                { icon: TrendingUp, label: 'Ton potentiel d\'amélioration' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm">
                  <div className="w-10 h-10 bg-rm-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-rm-primary" />
                  </div>
                  <span className="text-sm text-rm-text">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-rm-primary">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Prêt à analyser ton entreprise ?
            </h2>
            <p className="text-white/80 mb-8">Le diagnostic ne prend que 3 minutes</p>
            <Button 
              onClick={() => setCurrentStep(1)}
              className="bg-white hover:bg-white/90 text-rm-primary font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
            >
              Commencer maintenant
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </section>
      </div>
    );
  }

  // Steps 1-6: Questions
  if (currentStep >= 1 && currentStep <= 6) {
    const step = diagnosticSteps[currentStep - 1];
    const StepIcon = step.icon;

    return (
      <div className="min-h-screen bg-slate-50 py-8" data-testid="diagnostic-step">
        <DiagnosticSEO />
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm text-rm-text-secondary mb-2">
              <span>Étape {currentStep} / 6</span>
              <span>{Math.round((currentStep / 6) * 100)}%</span>
            </div>
            <div className="h-2 bg-white rounded-full overflow-hidden shadow-inner">
              <motion.div 
                initial={{ width: `${((currentStep - 1) / 6) * 100}%` }}
                animate={{ width: `${(currentStep / 6) * 100}%` }}
                className="h-full bg-gradient-to-r from-rm-primary to-rm-secondary rounded-full"
              />
            </div>
          </div>

          {/* Step header */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl p-6 shadow-lg mb-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-rm-primary/10 rounded-xl flex items-center justify-center">
                <StepIcon className="w-7 h-7 text-rm-primary" />
              </div>
              <div>
                <p className="text-sm text-rm-text-secondary">Étape {currentStep}</p>
                <h2 className="text-xl font-bold text-rm-text">{step.title}</h2>
              </div>
            </div>
            <p className="text-rm-text-secondary">
              {currentStep === 6 
                ? "Évaluez ta charge mentale d'entrepreneur"
                : "Combien de temps passes-tu sur ces tâches ?"}
            </p>
          </motion.div>

          {/* Tasks */}
          <div className="space-y-4 mb-8">
            {step.tasks.map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <TaskInput
                  task={task}
                  value={answers[task.id]}
                  onChange={handleAnswerChange}
                  stepId={step.id}
                />
              </motion.div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-4">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(prev => prev - 1)}
              className="rounded-full px-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
            <Button
              onClick={() => setCurrentStep(prev => prev + 1)}
              className="bg-rm-primary hover:bg-rm-primary/90 text-white rounded-full px-6"
            >
              {currentStep === 6 ? 'Voir mes résultats' : 'Suivant'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step 7: Email form
  if (currentStep === 7 || (!showResults && currentStep > 6)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rm-primary/5 to-rm-accent/10 py-20 flex items-center" data-testid="diagnostic-email">
        <DiagnosticSEO />
        <div className="container mx-auto px-4 md:px-6 max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-8 shadow-xl"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-rm-primary" />
              </div>
              <h2 className="text-2xl font-bold text-rm-text mb-2">
                Reçois ton diagnostic complet
              </h2>
              <p className="text-rm-text-secondary">
                Entre tes informations pour accéder à tes résultats détaillés et recevoir ton rapport personnalisé par email.
              </p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-rm-text mb-1">Prénom</label>
                <input
                  type="text"
                  required
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rm-primary/20 focus:border-rm-primary"
                  placeholder="Ton prénom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-rm-text mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rm-primary/20 focus:border-rm-primary"
                  placeholder="ton@email.com"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full py-4 text-lg shadow-lg hover:shadow-xl transition-all"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Recevoir mon diagnostic'}
                <Send className="w-5 h-5 ml-2" />
              </Button>
            </form>

            <p className="text-xs text-rm-text-secondary text-center mt-4">
              Tes données sont confidentielles et ne seront jamais partagées.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Step 8: Results
  if (showResults || currentStep === 8) {
    return (
      <div className="min-h-screen bg-slate-50 py-12" data-testid="diagnostic-results">
        <DiagnosticSEO />
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-rm-text mb-2">
              {prenom}, voici ton diagnostic
            </h1>
            <p className="text-rm-text-secondary">
              Analyse complète de ton organisation entrepreneuriale
            </p>
          </motion.div>

          {/* Scores */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ScoreGauge
                score={scores.efficiencyScore}
                label="Score d'efficacité entrepreneuriale"
                color="text-rm-primary"
                interpretation={scores.efficiencyInterpretation}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <ScoreGauge
                score={scores.libertyScore}
                label="Score de liberté entrepreneuriale"
                color="text-rm-secondary"
                interpretation={scores.libertyInterpretation}
              />
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
          >
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <Clock className="w-6 h-6 text-rm-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-rm-text">{scores.totalTimeWeekly}h</p>
              <p className="text-xs text-rm-text-secondary">par semaine</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <Calendar className="w-6 h-6 text-rm-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-rm-text">{scores.totalTimeMonthly}h</p>
              <p className="text-xs text-rm-text-secondary">par mois</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <TrendingUp className="w-6 h-6 text-rm-secondary mx-auto mb-2" />
              <p className="text-2xl font-bold text-rm-text">{scores.totalCost}€</p>
              <p className="text-xs text-rm-text-secondary">coûts mensuels</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <Users className="w-6 h-6 text-rm-secondary mx-auto mb-2" />
              <p className="text-2xl font-bold text-rm-text">{scores.selfTasksCount}</p>
              <p className="text-xs text-rm-text-secondary">tâches toi-même</p>
            </div>
          </motion.div>

          {/* Projection avec BatiAssist */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-rm-primary to-rm-primary/80 rounded-3xl p-8 text-white mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 text-center">
              Avec BatiAssist, ton score pourrait atteindre :
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <p className="text-white/80 mb-2">Efficacité</p>
                <p className="text-4xl font-bold">{scores.projectedEfficiency}/100</p>
                <p className="text-sm text-green-300 mt-2">+{scores.projectedEfficiency - scores.efficiencyScore} points</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <p className="text-white/80 mb-2">Liberté</p>
                <p className="text-4xl font-bold">{scores.projectedLiberty}/100</p>
                <p className="text-sm text-green-300 mt-2">+{scores.projectedLiberty - scores.libertyScore} points</p>
              </div>
            </div>
            <div className="space-y-3">
              <p className="font-medium mb-4">Grâce à :</p>
              {[
                "un assistant(e) dédié(e) 40h par semaine",
                "une équipe digitale disponible 24h/24",
                "un standard téléphonique intelligent",
                "une organisation administrative structurée",
                "une automatisation des tâches répétitives",
                "un système de visibilité et de prospection"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center bg-white rounded-3xl p-8 shadow-lg"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-rm-text mb-4">
              Et si ton entreprise fonctionnait enfin sans toi ?
            </h2>
            <p className="text-rm-text-secondary mb-8 max-w-2xl mx-auto">
              BatiAssist aide les entrepreneurs à structurer leur organisation grâce à un assistant à mi-temps ou plein temps et une équipe digitale.
            </p>
            <a 
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                className="bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
              >
                Réserver un entretien avec Romain
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
}
